import styled from '@emotion/styled';
import { Typography } from '@innovamat/glimmer-components';
import { IconType } from '@innovamat/glimmer-icons';
import { useSmartGuides } from '../../context/SmartGuidesProvider';
import { usePartColors } from '../../hooks/usePartColors';
import { PrintableBody2 } from '../../printable-styles/printable-typography';
import { Dimension } from '../../types/Dimension';
import { PartType } from '../../types/smart-guide';
import { IconEvaluablePrintable } from '../IconEvaluable';
import { IconPart } from '../IconPart';
import { PreprInnerHtml } from '../PreprInnerHtml';

type CollapsablePartProps = {
  part: PartType;
  momentTitle: string;
  activityTitle: string;
  activityTitleExtra?: string;
  children: React.ReactNode;
  partIcon?: IconType | undefined;
  evaluable?: {
    isEvaluable: boolean;
    dimension?: Dimension;
  };
  index: number;
};

const Summary = styled.div`
  break-inside: avoid;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MomentTitle = styled(Typography.SuperTitle)<{ color: string }>`
  color: ${({ color }) => color};
  font-size: 12px;

  @media print {
    font-size: 10px;
  }
`;

const ActivityTitle = styled(PrintableBody2)``;

const ActivityTitleExtra = styled(PrintableBody2)`
  display: inline;
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};

  @media print {
    color: ${({ theme }) =>
      theme.tokens.color.alias.cm.text['text-subtle'].value};
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  &.print-guide-part-gap {
    gap: 8px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledIconEvaluablePrintable = styled(IconEvaluablePrintable)`
  height: auto;
`;

const SummaryContainer = styled.div`
  break-inside: avoid;
`;

const SummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Subtitle = styled(PreprInnerHtml)`
  display: inline;
`;

export function CollapsablePartPrintable({
  part,
  momentTitle,
  activityTitle,
  activityTitleExtra,
  children,
  partIcon,
  evaluable,
  index,
}: CollapsablePartProps): JSX.Element {
  const { stage } = useSmartGuides();

  const { getElement } = usePartColors();

  const getSummary = (): JSX.Element => {
    return (
      <SummaryContainer>
        <Summary className={`part-summary-${index}`}>
          <TitleContainer>
            <IconPart part={part} stage={stage} icon={partIcon} />
            <Title>
              <MomentTitle color={getElement(part)}>{momentTitle}</MomentTitle>
              <ActivityTitle>
                <Subtitle text={activityTitle} />
                {activityTitleExtra && (
                  <ActivityTitleExtra>
                    &nbsp;{activityTitleExtra}
                  </ActivityTitleExtra>
                )}
              </ActivityTitle>
            </Title>
          </TitleContainer>

          {evaluable?.isEvaluable && (
            <StyledIconEvaluablePrintable dimension={evaluable.dimension} />
          )}
        </Summary>
        <SummaryContent
          className={`part-summary-content-${index}`}
        ></SummaryContent>
      </SummaryContainer>
    );
  };

  return (
    <Wrapper className={`collapsable-part-wrapper-${index}`}>
      {getSummary()}
      <Content className={`part-children-${index}`}>{children}</Content>
    </Wrapper>
  );
}
